// AggregatedGroup — карточка для сгруппированных уведомлений (напр. 3 артефакта
// по одному материалу). Тактильный редизайн «Картотека»: поверхность e1,
// hover-инсет, акцент-маркер при наличии непрочитанных, плавное раскрытие.

import { CategoryTile } from "@studyhub/ui/components/studyhub/category-tile";
import { IconChevronDown, IconSparkles } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";
import { NotificationItem, type NotificationItemData } from "./notification-item";

interface AggregatedGroupProps {
	title: string;
	items: NotificationItemData[];
	defaultExpanded?: boolean;
	onMarkRead?: (id: string) => void;
	/**
	 * `onDelete` / `onSelect` прокидываются в свёрнутые строки намеренно: без
	 * них раскрытая группа теряла бы удаление и закрытие мобильной шторки при
	 * переходе — то есть подключение группировки молча урезало бы то, что
	 * умеет обычная строка списка.
	 */
	onDelete?: (id: string) => void;
	onSelect?: () => void;
}

export function AggregatedGroup({
	title,
	items,
	defaultExpanded = false,
	onMarkRead,
	onDelete,
	onSelect,
}: AggregatedGroupProps) {
	const { t } = useTranslation("notifications");
	const [expanded, setExpanded] = useState(defaultExpanded);
	const unreadCount = items.filter((i) => !i.read).length;
	const hasUnread = unreadCount > 0;

	return (
		<div
			className={cn(
				"overflow-hidden rounded-[var(--radius-card)] border border-border bg-card shadow-e1",
				hasUnread && "bg-primary/[0.04]",
			)}
		>
			<button
				type="button"
				onClick={() => setExpanded((s) => !s)}
				aria-expanded={expanded}
				className={cn(
					"relative flex w-full cursor-pointer items-center gap-3 p-3 pl-4 text-left",
					"transition-colors duration-fast hover:bg-muted/40",
				)}
			>
				{hasUnread && (
					<span
						aria-hidden
						className="absolute inset-y-1.5 left-0 w-[3px] rounded-full bg-primary"
					/>
				)}
				<div className="relative">
					<CategoryTile category="ai" icon={<IconSparkles stroke={1.75} />} size="md" />
					<span className="absolute -right-1.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold tabular-nums text-white shadow-e1">
						{items.length}
					</span>
				</div>
				<div className="min-w-0 flex-1">
					<div className="truncate text-sm font-semibold text-foreground">
						{t("aggregated.title", { count: items.length })}
					</div>
					<div className="mt-0.5 truncate text-xs text-muted-foreground">{title}</div>
				</div>
				{hasUnread && (
					<span
						aria-hidden
						className="size-2 shrink-0 rounded-full bg-primary ring-[3px] ring-primary/15"
					/>
				)}
				<IconChevronDown
					size={16}
					className={cn(
						"sh-disclose-chevron shrink-0 text-muted-foreground",
						expanded && "rotate-180",
					)}
				/>
			</button>
			<div
				className="sh-disclose"
				data-state={expanded ? "expanded" : "collapsed"}
				aria-hidden={!expanded}
				inert={!expanded}
			>
				<div className="sh-disclose-inner">
					<div className="space-y-1.5 border-t border-border bg-muted/20 p-2">
						{items.map((item) => (
							<NotificationItem
								key={item.id}
								item={item}
								onMarkRead={onMarkRead}
								onDelete={onDelete}
								onSelect={onSelect}
								compact
							/>
						))}
					</div>
				</div>
			</div>
		</div>
	);
}
